/* RESPONSIBLE TEAM: team-conversational-knowledge */
import Service from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { type TopicItem } from 'embercom/components/reporting/automation/ai-insights/topics-side-panel';
import type AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import { type EntityType } from 'embercom/models/data/entity-types';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';
import RouteRegexes from 'embercom/lib/route-regexes';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';

export default class ContentSuggestionsService extends Service {
  @service declare router: RouterService;

  @tracked declare listApi: AiContentLibraryApi;
  @tracked selectedTopicItems?: Array<ContentWrapper>;
  @tracked topics: Array<TopicItem> = [];

  get isOnSuggestionsPage() {
    return RouteRegexes.automation.contentSuggestions.test(this.router.currentRouteName);
  }

  async refreshSuggestionsListWithDelay() {
    // On the AI insights page we are optimistically removing the item from the list, and then immediately refreshing it without a delay.
    // On the content suggestions page we are not removing the item from the list, giving ES time to update and then refreshing it.
    if (!this.isOnSuggestionsPage) {
      return;
    }

    if (!this.listApi) {
      return;
    }

    // Let's give ES some time to update
    await timeout(ENV.APP._500MS);
    this.listApi.resetFiltersForSearch();
    await this.listApi.loadPage();
  }

  removeSuggestionFromList(content: { entity_id: number; entity_type: EntityType }) {
    this.removeFromContentWrapperList(content);
    this.removeFromSelectedTopicItems(content);
    this.updateTopicList();
  }

  updateTopicList() {
    this.topics = [];
    this.listApi?.contentWrappers.forEach((wrapper) => {
      let topic = wrapper.contentReviewSuggestionMetadata.topic;
      let existing = this.topics.find((t) => t.topic === topic);

      if (existing) {
        existing.count++;
        existing.items = [...existing.items, wrapper];
        wrapper.contentReviewSuggestionMetadata.included_conversations?.forEach(
          (conversationId: number) => {
            existing?.conversationIds.add(conversationId);
          },
        );
      } else {
        let conversationIds = new Set<number>();
        wrapper.contentReviewSuggestionMetadata.included_conversations?.forEach(
          (conversationId: number) => {
            conversationIds.add(conversationId);
          },
        );
        this.topics.push({ topic, count: 1, items: [wrapper], conversationIds });
      }
    });
    this.topics = this.topics
      .filter((topic) => !isEmpty(topic.topic))
      .sort((a, b) => b.conversationIds.size - a.conversationIds.size);
  }

  private removeFromContentWrapperList(content: { entity_id: number; entity_type: EntityType }) {
    if (this.listApi) {
      let index = this.listApi.contentWrappers.findIndex(
        (wrapper) =>
          wrapper.entityId === content.entity_id && wrapper.entityType === content.entity_type,
      );
      if (index !== -1) {
        this.listApi.contentWrappers = [
          ...this.listApi.contentWrappers.slice(0, index),
          ...this.listApi.contentWrappers.slice(index + 1),
        ];
      }
    }
  }

  private removeFromSelectedTopicItems(content: { entity_id: number; entity_type: EntityType }) {
    if (this.selectedTopicItems) {
      let index = this.selectedTopicItems.findIndex(
        (wrapper) =>
          wrapper.entityId === content.entity_id && wrapper.entityType === content.entity_type,
      );
      if (index !== -1) {
        this.selectedTopicItems = [
          ...this.selectedTopicItems.slice(0, index),
          ...this.selectedTopicItems.slice(index + 1),
        ];
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    contentSuggestionsService: ContentSuggestionsService;
    'content-suggestions-service': ContentSuggestionsService;
  }
}
